import { Avatar, CloseButton, Combobox, Group, InputBase, Text, useCombobox } from '@mantine/core';
import { useDebouncedState } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useState } from 'react';
import { type LightDebtor, useLoadLightDebtors } from 'shared/hooks/use-load-light-debtors';

type DebtorSelectProps = {
  value: string | null;
  onChange: (debtorId: string | null, debtor: LightDebtor | null) => void;
  error?: string;
};

export const DebtorSelect = ({ value, onChange, error }: DebtorSelectProps) => {
  const { t } = useTranslation();

  const [rawInput, setRawInput] = useState('');
  const [searchValue, setSearchValue] = useDebouncedState('', 200);
  const [nameCache, setNameCache] = useState<Record<string, string>>({});

  const { lightDebtors } = useLoadLightDebtors({
    query: searchValue,
    externalId: searchValue,
  });

  useEffect(() => {
    if (lightDebtors.length > 0) {
      setNameCache(prev => ({
        ...prev,
        ...Object.fromEntries(lightDebtors.map(d => [d.id, d.fullName])),
      }));
    }
  }, [lightDebtors]);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setRawInput('');
      setSearchValue('');
    },
  });

  const options = [...(lightDebtors ?? [])];
  // Keep the currently selected debtor visible even when not in current search results
  if (value != null && !options.some(d => d.id === value))
    options.unshift({ id: value, fullName: nameCache[value] ?? value });

  const displayName = value != null ? (nameCache[value] ?? value) : '';
  const visibleValue = combobox.dropdownOpened ? rawInput : displayName;

  const handleSelect = (selectedId: string) => {
    const found = (lightDebtors ?? []).find(d => d.id === selectedId) ?? null;
    onChange(selectedId, found);
    combobox.closeDropdown();
  };

  const handleClear = () => {
    onChange(null, null);
    setRawInput('');
    setSearchValue('');
  };

  return (
    <Combobox store={combobox} onOptionSubmit={handleSelect} width="target" withinPortal={false}>
      <Combobox.Target>
        <InputBase
          label={t(i18nKeys.TASK.ATTRIBUTES.DEBTOR)}
          placeholder={t(i18nKeys.EXTERNAL_MAIL.SELECT_DEBTOR)}
          value={visibleValue}
          error={error}
          onChange={event => {
            const text = event.currentTarget.value;
            setRawInput(text);
            setSearchValue(text);
            combobox.openDropdown();
          }}
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => combobox.closeDropdown()}
          rightSection={
            value != null ? (
              <CloseButton
                size="sm"
                onMouseDown={event => event.preventDefault()}
                onClick={handleClear}
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          rightSectionPointerEvents={value != null ? 'all' : 'none'}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={300} style={{ overflowY: 'auto' }}>
          {options.length === 0 ? (
            <Combobox.Empty>{t(i18nKeys.NO_RESULT)}</Combobox.Empty>
          ) : (
            options.map(debtor => {
              const externalIdentifier = debtor.externalId ?? debtor.csvId ?? null;
              const isSelected = debtor.id === value;

              return (
                <Combobox.Option value={debtor.id} key={debtor.id} active={isSelected}>
                  <Group wrap="nowrap" gap="sm">
                    <Avatar size={36} radius="xl" name={debtor.fullName} color="initials" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} truncate>
                        {debtor.fullName}
                      </Text>
                      {externalIdentifier != null && (
                        <Text size="xs" c="dimmed" ff="monospace" truncate>
                          {externalIdentifier}
                        </Text>
                      )}
                    </div>
                    {isSelected && (
                      <IconCheck
                        size={16}
                        color="var(--mantine-color-blue-6)"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                  </Group>
                </Combobox.Option>
              );
            })
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
